import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import { API_BASE_URL } from "./config";
import { useNavigate, Link, useLocation } from "react-router-dom";
import "./audio.css"
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AuthenticatedAudio = ({ url, token }) => {
    const [audioSrc, setAudioSrc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    
    // Player State
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [gain, setGain] = useState(1); // 0 to 3
    const [playbackRate, setPlaybackRate] = useState(1);
    const [showSpeedMenu, setShowSpeedMenu] = useState(false);
    
    const audioRef = useRef(null);
    const audioContextRef = useRef(null);
    const sourceRef = useRef(null);
    const gainNodeRef = useRef(null);

    useEffect(() => {
        return () => {
            if (audioSrc) URL.revokeObjectURL(audioSrc);
            if (audioContextRef.current) {
                audioContextRef.current.close();
                audioContextRef.current = null;
            }
        };
    }, [audioSrc]);

    const loadAudio = async () => {
        if (audioSrc) return;
        setLoading(true);
        try {
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error(response.statusText);
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setAudioSrc(objectUrl);
        } catch (err) {
            console.error("Error loading audio", err);
            setError(true);
            toast.error("Erro ao carregar áudio");
        } finally {
            setLoading(false);
        }
    };

    const initAudioContext = () => {
        if (!audioRef.current || audioContextRef.current) return;

        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            const ctx = new AudioContext();
            audioContextRef.current = ctx;

            const source = ctx.createMediaElementSource(audioRef.current);
            sourceRef.current = source;

            // Compressor to prevent clipping
            const compressor = ctx.createDynamicsCompressor();
            compressor.threshold.setValueAtTime(-24, ctx.currentTime);
            compressor.knee.setValueAtTime(30, ctx.currentTime);
            compressor.ratio.setValueAtTime(12, ctx.currentTime);
            compressor.attack.setValueAtTime(0.003, ctx.currentTime);
            compressor.release.setValueAtTime(0.25, ctx.currentTime);

            const gainNode = ctx.createGain();
            gainNode.gain.value = gain;
            gainNodeRef.current = gainNode;

            source.connect(compressor);
            compressor.connect(gainNode);
            gainNode.connect(ctx.destination);
        } catch (e) {
            console.error("Audio Context Init Error", e);
        }
    };

    useEffect(() => {
        if (audioSrc && audioRef.current) {
            initAudioContext();
        }
    }, [audioSrc]);

    // Handle Volume/Gain Change
    useEffect(() => {
        if (gainNodeRef.current) {
            gainNodeRef.current.gain.value = gain;
        }
    }, [gain]);

    // Handle Playback Rate Change
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.playbackRate = playbackRate;
        }
    }, [playbackRate]);

    // Audio Event Handlers
    const togglePlay = () => {
        if (!audioRef.current) return;
        
        // Ensure context is running
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume();
        }

        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
            // Auto play logic if desired, or let user click play
            // Since we load on "Reproduzir" click, we might want to auto-play
            audioRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        }
    };

    const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        if (audioRef.current) audioRef.current.currentTime = 0;
    };

    const handleSeek = (e) => {
        const time = parseFloat(e.target.value);
        if (audioRef.current) {
            audioRef.current.currentTime = time;
            setCurrentTime(time);
        }
    };

    const formatTime = (time) => {
        if (isNaN(time)) return "0:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    if (error) return <div className="text-danger text-center small p-2">Erro ao carregar</div>;

    if (audioSrc) {
        return (
            <div className="w-100">
                <audio 
                    ref={audioRef}
                    src={audioSrc}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={handleEnded}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    controlsList="nodownload"
                    className="d-none" 
                />
                
                {/* Responsive Compact Player UI */}
                <div className="d-flex flex-wrap flex-md-nowrap align-items-center gap-3 w-100">
                    
                    {/* Play/Pause Button */}
                    <button 
                        className="btn btn-primary btn-sm rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: '40px', height: '40px' }}
                        onClick={togglePlay}
                    >
                        <Icon icon={isPlaying ? "solar:pause-bold" : "solar:play-bold"} width="22" />
                    </button>
                    
                    {/* Progress Bar & Time */}
                    <div className="d-flex align-items-center gap-2 flex-grow-1 w-100 order-3 order-md-1">
                        <span className="text-xs text-secondary-light fw-medium" style={{ minWidth: '35px', textAlign: 'center' }}>
                            {formatTime(currentTime)}
                        </span>
                        <input 
                            type="range" 
                            min="0" 
                            max={duration || 100} 
                            value={currentTime} 
                            onChange={handleSeek}
                            className="form-range flex-grow-1 cursor-pointer"
                            style={{ height: '4px' }}
                        />
                        <span className="text-xs text-secondary-light fw-medium" style={{ minWidth: '35px', textAlign: 'center' }}>
                            {formatTime(duration)}
                        </span>
                    </div>

                    {/* Volume/Boost Control */}
                    <div className="d-flex align-items-center gap-2 flex-shrink-0 order-1 order-md-2 ms-auto ms-md-0">
                        <div className="d-flex align-items-center gap-2 bg-base border border-neutral-200 rounded-pill px-3 py-1" style={{ height: '32px' }}>
                            <Icon 
                                icon={gain === 0 ? "solar:volume-cross-bold" : (gain > 1 ? "solar:volume-loud-bold" : "solar:volume-small-bold")} 
                                className={`flex-shrink-0 ${gain > 1 ? 'text-primary-600' : 'text-secondary-light'}`}
                                width="18" 
                            />
                            <input 
                                type="range" 
                                min="0" 
                                max="3" 
                                step="0.1" 
                                value={gain} 
                                onChange={(e) => setGain(parseFloat(e.target.value))}
                                className="form-range cursor-pointer"
                                style={{ width: '60px', height: '4px' }}
                                title="Volume e Boost (até 300%)"
                            />
                            <span className={`text-xs fw-bold ${gain > 1 ? 'text-primary-600' : 'text-secondary-light'}`} style={{ minWidth: '36px', textAlign: 'right' }}>
                                {Math.round(gain * 100)}%
                            </span>
                        </div>
                    </div>

                    {/* Playback Speed Control */}
                    <div className="position-relative flex-shrink-0 order-2 order-md-3">
                        <button 
                            className="d-flex align-items-center justify-content-between gap-1 bg-base border border-neutral-200 text-secondary-light fw-bold cursor-pointer rounded-pill px-3"
                            style={{ height: '32px', fontSize: '0.8rem', minWidth: '75px' }}
                            onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                            onBlur={() => setShowSpeedMenu(false)}
                        >
                            <span>{playbackRate}x</span>
                            <Icon icon="solar:alt-arrow-down-bold" width="12" />
                        </button>
                        
                        {showSpeedMenu && (
                            <div 
                                className="position-absolute top-100 end-0 mt-1 bg-base border border-neutral-200 rounded-3 shadow-sm overflow-hidden"
                                style={{ zIndex: 10, minWidth: '100%' }}
                            >
                                {[0.5, 0.75, 1, 1.25, 1.5, 2].map(rate => (
                                    <button
                                        key={rate}
                                        className={`d-block w-100 text-start px-3 py-2 border-0 bg-transparent ${playbackRate === rate ? 'text-primary-600 fw-bold bg-primary-50' : 'text-secondary-light hover-bg-neutral-50'}`}
                                        style={{ fontSize: '0.8rem' }}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            setPlaybackRate(rate);
                                            setShowSpeedMenu(false);
                                        }}
                                    >
                                        {rate}x
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex align-items-center justify-content-center w-100" style={{ minHeight: '54px' }}>
            {loading ? (
                <div className="spinner-border text-primary spinner-border-sm" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </div>
            ) : (
                <button 
                    className="btn btn-primary btn-sm rounded-pill px-3 d-flex align-items-center gap-2" 
                    onClick={loadAudio}
                >
                    <Icon icon="solar:play-bold" />
                    <span>Reproduzir</span>
                </button>
            )}
        </div>
    );
};

const Audios = () => {
    const [cursos, setCursos] = useState([]);
    const [audios, setAudios] = useState([]);
    const [paginaAtual, setPaginaAtual] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [itemsPerPageCursos, setItemsPerPageCursos] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortDirection, setSortDirection] = useState("asc");
    const [selectedCursoId, setSelectedCursoId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [paginaAtualCursos, setPaginaAtualCursos] = useState(1);
    const navigate = useNavigate();
    const location = useLocation();
    const returnTo = `${location.pathname}${location.search}${location.hash || ""}`;
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [cursoParaExcluir, setCursoParaExcluir] = useState(null);
    const [impactoExclusao, setImpactoExclusao] = useState(null);
    const [carregandoImpacto, setCarregandoImpacto] = useState(false);
    const [deletandoCurso, setDeletandoCurso] = useState(false);
    const [showDeleteAudioModal, setShowDeleteAudioModal] = useState(false);
    const [audioParaExcluir, setAudioParaExcluir] = useState(null);

    const tipoUser = localStorage.getItem("userType");
    const editarCurso = (idCurso) => {
        navigate(`/cadastro-audio/${idCurso}`, { state: { from: returnTo } });
    };

    useEffect(() => {
        fetchCursos();
    }, []);

    const fecharModalExcluir = () => {
        setShowDeleteModal(false);
        setCursoParaExcluir(null);
        setImpactoExclusao(null);
        setCarregandoImpacto(false);
    };

    const abrirModalExcluir = async (curso) => {
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error("Token não encontrado. Faça login novamente.");
            return;
        }

        setCursoParaExcluir(curso.cp_curso_id);
        setImpactoExclusao(null);
        setCarregandoImpacto(true);
        setShowDeleteModal(true);

        try {
            const response = await axios.get(
                `${API_BASE_URL}/cursos/${curso.cp_curso_id}/impacto-exclusao`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setImpactoExclusao(response.data);
        } catch (error) {
            console.error("Erro ao consultar impacto da exclusão:", error);
            toast.error(error.response?.data?.msg || "Erro ao consultar vínculos do curso");
            fecharModalExcluir();
        } finally {
            setCarregandoImpacto(false);
        }
    };

    const fecharModalExcluirAudio = () => {
        setShowDeleteAudioModal(false);
        setAudioParaExcluir(null);
    };

    const deletarCurso = async () => {
        if (!cursoParaExcluir || !impactoExclusao?.podeExcluir || deletandoCurso) return;
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error("Token não encontrado. Faça login novamente.");
            return;
        }

        try {
            setDeletandoCurso(true);
            const headers = { Authorization: `Bearer ${token}` };
            await axios.delete(`${API_BASE_URL}/delete-curso/${cursoParaExcluir}`, { headers });
            toast.success("Curso e áudios excluídos com sucesso");

            fecharModalExcluir();
            if (selectedCursoId === cursoParaExcluir) {
                setSelectedCursoId(null);
                setAudios([]);
            }
            fetchCursos();
        } catch (error) {
            console.error("Erro ao excluir curso:", error);
            toast.error(error.response?.data?.msg || "Erro ao excluir curso");
        } finally {
            setDeletandoCurso(false);
        }
    };

    const deletarAudio = async () => {
        if (!audioParaExcluir?.cp_audio_id) return;
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error("Token não encontrado. Faça login novamente.");
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/audio/${audioParaExcluir.cp_audio_id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Áudio excluído com sucesso");

            setAudios((prev) => {
                const next = prev.filter((a) => a.cp_audio_id !== audioParaExcluir.cp_audio_id);
                setPaginaAtual((prevPage) => Math.min(prevPage, Math.max(1, Math.ceil(next.length / 10))));
                if (next.length === 0) {
                    setSelectedCursoId(null);
                }
                return next;
            });

            fecharModalExcluirAudio();
            fetchCursos();
        } catch (error) {
            console.error("Erro ao excluir áudio:", error);
            toast.error("Erro ao excluir áudio");
        }
    };


    const fetchCursos = async () => {
        const professorId = localStorage.getItem('userId');
        const tipoUsuario = localStorage.getItem('userType');

        if (tipoUsuario === "1") {
            try {
                const response = await fetch(`${API_BASE_URL}/cursos`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                const todosCursos = await response.json();
                todosCursos.sort((a, b) => a.cp_nome_curso.localeCompare(b.cp_nome_curso, undefined, { numeric: true, sensitivity: 'base' }));
                setCursos(todosCursos);

            } catch (error) {
                console.error("Erro ao buscar todos os cursos:", error);
            }
            return;
        }

        try {
            const responseTurmas = await fetch(`${API_BASE_URL}/cp_turmas/professor/${professorId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            const turmas = await responseTurmas.json();
            const cursoIds = turmas.map(turma => turma.cp_tr_curso_id);

            if (cursoIds.length > 0) {
                const responseCursos = await fetch(`${API_BASE_URL}/cursos/batch`, {
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('token')}` 
                    },
                    body: JSON.stringify({ cursoIds })
                });
                const cursos = await responseCursos.json();
                cursos.sort((a, b) => a.cp_nome_curso.localeCompare(b.cp_nome_curso, undefined, { numeric: true, sensitivity: 'base' }));
                setCursos(cursos);

            } else {
                setCursos([]);
            }
        } catch (error) {
            console.error("Erro ao buscar cursos:", error);
        }
    };




    const fetchAudios = async (cursoId) => {
        setLoading(true);
        setPaginaAtual(1); // Sempre volta para a primeira página ao trocar de curso
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error("Token não encontrado. Faça login novamente.");
                return;
            }

            const response = await fetch(`${API_BASE_URL}/audios-curso/${cursoId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    toast.error("Sessão expirada ou inválida. Faça login novamente.");
                } else {
                    toast.error(`Erro ao buscar áudios: ${response.status}`);
                }
                return;
            }

            const data = await response.json();
            data.sort((a, b) => a.cp_nome_audio.localeCompare(b.cp_nome_audio, undefined, { numeric: true, sensitivity: 'base' }));
            setAudios(data);
            setSelectedCursoId(cursoId);
        } catch (error) {
            console.error("Erro ao buscar áudios:", error);
            toast.error("Erro de conexão ao buscar áudios.");
        } finally {
            setLoading(false);
        }
    };

    const handleSortChange = () => {
        const newDirection = sortDirection === "asc" ? "desc" : "asc";
        setSortDirection(newDirection);
        const sortedCursos = [...cursos].sort((a, b) => {
            const nomeA = a.cp_nome_curso.toLowerCase();
            const nomeB = b.cp_nome_curso.toLowerCase();
            return newDirection === "asc"
                ? nomeA.localeCompare(nomeB)
                : nomeB.localeCompare(nomeA);
        });
        setCursos(sortedCursos);
    };

    const filteredAudios = audios.slice(
        (paginaAtual - 1) * 10, // Mantém fixo em 10 itens por página
        paginaAtual * 10
    );

    const totalPaginasAudiosCurso = Math.ceil(audios.length / 10); // Também fixo em 10


    const filteredCursos = cursos.filter((curso) =>
        curso.cp_nome_curso.toLowerCase().includes(searchTerm.toLowerCase())
    );


    const currentCursos =
        itemsPerPageCursos === "all"
            ? filteredCursos
            : filteredCursos.slice(
                (paginaAtualCursos - 1) * itemsPerPageCursos,
                paginaAtualCursos * itemsPerPageCursos
            );

    const totalPaginasCursos =
        itemsPerPageCursos === "all"
            ? 1
            : Math.ceil(filteredCursos.length / itemsPerPageCursos);


    const totalPaginas = itemsPerPage === "all" ? 1 : Math.ceil(filteredCursos.length / itemsPerPage);


    return (
        <>
            <style jsx>{`
                .audio-cards-container {
                    padding: 0;
                }

                .audio-card {
                    transition: all 0.3s ease;
                    border-radius: 12px !important;
                }

                .audio-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(0,0,0,0.1) !important;
                }

                .audio-title {
                    font-size: 0.75rem;
                    font-weight: 500;
                    line-height: 1.3;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .audio-player-wrapper {
                    width: 100%;
                }

                .audio-player {
                    height: 40px;
                    border-radius: 8px;
                }

                .audio-player::-webkit-media-controls-panel {
                    background-color: #f8f9fa;
                    border-radius: 8px;
                }
                
                .cursor-pointer {
                    cursor: pointer;
                }
                .transition-2 {
                    transition: all 0.2s ease-in-out;
                }
            `}</style>
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <span className="text-md fw-medium text-secondary-light mb-0">Mostrar</span>
                    <select
                        className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px bg-base"
                        value={itemsPerPageCursos}
                        onChange={(e) => {
                            const value = e.target.value === "all" ? "all" : Number(e.target.value);
                            setItemsPerPageCursos(value);
                            setPaginaAtualCursos(1); // Reinicia para a primeira página de cursos
                        }}
                    >
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="30">30</option>
                        <option value="all">Ver Todos</option>
                    </select>


                    <form className="navbar-search">
                        <input
                            type="text"
                            className="bg-base h-40-px w-auto"
                            placeholder="Pesquisar"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Icon icon="ion:search-outline" className="icon" />
                    </form>
                    <button
                        className="btn btn-outline-secondary text-md py-6 radius-12 h-40-px d-flex align-items-center gap-2"
                        onClick={handleSortChange}
                    >
                        Ordenar por {sortDirection === "asc" ? "A-Z" : "Z-A"}
                    </button>
                </div>
            </div>
            <div className="row">
                <div className="col-12 col-md-4 border-md-end mb-3 mb-md-0">
                    <div className="card-body p-24">
                        <ul className="d-flex flex-column gap-2 p-0">
                            {currentCursos.map((curso) => (
                                <li
                                    key={curso.cp_curso_id}
                                    className={`d-flex justify-content-between align-items-center p-3 radius-8 border cursor-pointer transition-2 ${
                                        selectedCursoId === curso.cp_curso_id
                                            ? "bg-primary-50 border-primary-100"
                                            : "bg-base border-neutral-200 bg-hover-neutral-50"
                                    }`}
                                    onClick={() => fetchAudios(curso.cp_curso_id)}
                                >
                                    <span className={`fw-medium ${selectedCursoId === curso.cp_curso_id ? "text-primary-600" : "text-primary-light"}`}>
                                        {curso.cp_nome_curso}
                                    </span>
                                    
                                    <div className="d-flex align-items-center gap-2">
                                        {tipoUser === "1" && (
                                            <Link
                                                to={`/cadastro-audio/${curso.cp_curso_id}`}
                                                state={{ from: returnTo }}
                                                className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Icon icon="lucide:edit" />
                                            </Link>
                                        )}
                                        {tipoUser === "1" && (
                                            <button
                                                type="button"
                                                className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    abrirModalExcluir(curso);
                                                }}
                                                title="Excluir"
                                            >
                                                <Icon icon="lucide:trash-2" />
                                            </button>
                                        )}
                                        <div className={`w-32-px h-32-px rounded-circle d-flex align-items-center justify-content-center ${selectedCursoId === curso.cp_curso_id ? "bg-primary-600 text-white" : "bg-neutral-200 text-secondary-light"}`}>
                                            <Icon icon="ep:arrow-right" />
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <div className="d-flex flex-column align-items-center justify-content-center mt-24">
                            <div className="d-flex align-items-center justify-content-between w-100 mb-3">
                                <span className="text-sm text-secondary-light">
                                    Página {paginaAtualCursos} de {totalPaginasCursos}
                                </span>
                                <select
                                    className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px bg-base"
                                    value={paginaAtualCursos}
                                    onChange={(e) => setPaginaAtualCursos(Number(e.target.value))}
                                >
                                    {Array.from({ length: totalPaginasCursos }, (_, idx) => (
                                        <option key={idx + 1} value={idx + 1}>
                                            Página {idx + 1}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center m-0">
                                <li className="page-item">
                                    <button
                                        className="page-link bg-base border-neutral-200 text-secondary-light fw-semibold radius-8 border d-flex align-items-center justify-content-center h-32-px w-32-px"
                                        onClick={() => setPaginaAtualCursos(1)}
                                        disabled={paginaAtualCursos === 1}
                                    >
                                        <Icon icon="ep:d-arrow-left" />
                                    </button>
                                </li>
                                <li className="page-item">
                                    <button
                                        className="page-link bg-base border-neutral-200 text-secondary-light fw-semibold radius-8 border d-flex align-items-center justify-content-center h-32-px px-3"
                                        onClick={() => setPaginaAtualCursos(prev => Math.max(prev - 1, 1))}
                                        disabled={paginaAtualCursos === 1}
                                    >
                                        Anterior
                                    </button>
                                </li>
                                {Array.from({ length: totalPaginasCursos }, (_, idx) => idx + 1)
                                    .filter(page => page === 1 || page === totalPaginasCursos || (page >= paginaAtualCursos - 2 && page <= paginaAtualCursos + 2))
                                    .map((page, idx, pages) => {
                                        if (idx > 0 && page > pages[idx - 1] + 1) {
                                            return (
                                                <li key={`ellipsis-${idx}`} className="page-item">
                                                    <span className="page-link bg-base border-neutral-200 text-secondary-light fw-semibold radius-8 border d-flex align-items-center justify-content-center h-32-px w-32-px">
                                                        ...
                                                    </span>
                                                </li>
                                            );
                                        }
                                        return (
                                            <li key={page} className="page-item">
                                                <button
                                                    className={`page-link fw-semibold radius-8 border d-flex align-items-center justify-content-center h-32-px w-32-px ${
                                                        paginaAtualCursos === page 
                                                        ? "bg-primary-600 text-white border-primary-600" 
                                                        : "bg-base border-neutral-200 text-secondary-light hover-bg-neutral-200"
                                                    }`}
                                                    onClick={() => setPaginaAtualCursos(page)}
                                                >
                                                    {page}
                                                </button>
                                            </li>
                                        );
                                    })}
                                <li className="page-item">
                                    <button
                                        className="page-link bg-base border-neutral-200 text-secondary-light fw-semibold radius-8 border d-flex align-items-center justify-content-center h-32-px px-3"
                                        onClick={() => setPaginaAtualCursos(prev => Math.min(prev + 1, totalPaginasCursos))}
                                        disabled={paginaAtualCursos === totalPaginasCursos}
                                    >
                                        Próximo
                                    </button>
                                </li>
                                <li className="page-item">
                                    <button
                                        className="page-link bg-base border-neutral-200 text-secondary-light fw-semibold radius-8 border d-flex align-items-center justify-content-center h-32-px w-32-px"
                                        onClick={() => setPaginaAtualCursos(totalPaginasCursos)}
                                        disabled={paginaAtualCursos === totalPaginasCursos}
                                    >
                                        <Icon icon="ep:d-arrow-right" />
                                    </button>
                                </li>
                            </ul>
                        </div>

                    </div>
                </div>
                <div className="col-12 col-md-8">
                    <div className="card-body p-24">
                        {(!selectedCursoId || audios.length === 0) && !loading ? (
                            <div className="fw-bold text-primary">Clique em "Ver Áudios"</div>
                        ) : (
                            <div className="audio-cards-container">
                                {loading ? (
                                    <div className="text-center p-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Carregando...</span>
                                        </div>
                                        <p className="mt-2">Carregando áudios...</p>
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-3">
                                        {filteredAudios.map((audio) => (
                                            <div key={audio.cp_audio_id} className="w-100">
                                                <div className="audio-card card border shadow-none hover-shadow-sm transition-2 radius-12">
                                                    <div className="card-body p-3">
                                                        <div className="d-flex align-items-center gap-3 mb-3">
                                                            <div className="w-40-px h-40-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center flex-shrink-0">
                                                                <Icon icon="solar:music-note-bold" width="20" />
                                                            </div>
                                                            <h6 className="audio-title mb-0 flex-grow-1 text-primary-light" title={audio.cp_nome_audio}>
                                                                {audio.cp_nome_audio.replace(/\.[^/.]+$/, "")}
                                                            </h6>
                                                            {tipoUser === "1" && (
                                                                <button
                                                                    type="button"
                                                                    className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0 flex-shrink-0"
                                                                    onClick={() => {
                                                                        setAudioParaExcluir(audio);
                                                                        setShowDeleteAudioModal(true);
                                                                    }}
                                                                    title="Excluir"
                                                                >
                                                                    <Icon icon="lucide:trash-2" />
                                                                </button>
                                                            )}
                                                        </div>
                                                            <div className="audio-player-wrapper bg-base border border-neutral-200 rounded-3 p-2">
                                                                <AuthenticatedAudio 
                                                                    url={`${API_BASE_URL}/audio/${audio.cp_nome_audio}`} 
                                                                    token={localStorage.getItem('token')} 
                                                                />
                                                            </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="d-flex flex-column flex-md-row align-items-center justify-content-between mt-24 gap-3">
                            <span className="text-sm text-secondary-light">
                                Mostrando {paginaAtual} de {totalPaginasAudiosCurso} páginas
                            </span>
                            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center m-0">
                                <li className="page-item">
                                    <button
                                        className="page-link bg-base border-neutral-200 text-secondary-light fw-semibold radius-8 border d-flex align-items-center justify-content-center h-32-px w-32-px"
                                        onClick={() => setPaginaAtual(1)}
                                        disabled={paginaAtual === 1}
                                    >
                                        <Icon icon="ep:d-arrow-left" />
                                    </button>
                                </li>
                                <li className="page-item">
                                    <button
                                        className="page-link bg-base border-neutral-200 text-secondary-light fw-semibold radius-8 border d-flex align-items-center justify-content-center h-32-px px-3"
                                        onClick={() => setPaginaAtual((prev) => Math.max(prev - 1, 1))}
                                        disabled={paginaAtual === 1}
                                    >
                                        Anterior
                                    </button>
                                </li>
                                {Array.from({ length: totalPaginasAudiosCurso }, (_, idx) => idx + 1)
                                    .filter((page) => {
                                        return (
                                            page === 1 ||
                                            page === totalPaginasAudiosCurso ||
                                            (page >= paginaAtual - 2 && page <= paginaAtual + 2)
                                        );
                                    })
                                    .map((page, idx, pages) => {
                                        if (idx > 0 && page > pages[idx - 1] + 1) {
                                            return (
                                                <li key={`ellipsis-${idx}`} className="page-item">
                                                    <span className="page-link bg-base border-neutral-200 text-secondary-light fw-semibold radius-8 border d-flex align-items-center justify-content-center h-32-px w-32-px">
                                                        ...
                                                    </span>
                                                </li>
                                            );
                                        }
                                        return (
                                            <li
                                                key={page}
                                                className="page-item"
                                            >
                                                <button
                                                    className={`page-link fw-semibold radius-8 border d-flex align-items-center justify-content-center h-32-px w-32-px ${paginaAtual === page
                                                        ? "bg-primary-600 text-white border-primary-600"
                                                        : "bg-base border-neutral-200 text-secondary-light hover-bg-neutral-200"
                                                        }`}
                                                    onClick={() => setPaginaAtual(page)}
                                                >
                                                    {page}
                                                </button>
                                            </li>
                                        );
                                    })}
                                <li className="page-item">
                                    <button
                                        className="page-link bg-base border-neutral-200 text-secondary-light fw-semibold radius-8 border d-flex align-items-center justify-content-center h-32-px px-3"
                                        onClick={() => setPaginaAtual((prev) => Math.min(prev + 1, totalPaginasAudiosCurso))}
                                        disabled={paginaAtual === totalPaginasAudiosCurso}
                                    >
                                        Próximo
                                    </button>
                                </li>
                                <li className="page-item">
                                    <button
                                        className="page-link bg-base border-neutral-200 text-secondary-light fw-semibold radius-8 border d-flex align-items-center justify-content-center h-32-px w-32-px"
                                        onClick={() => setPaginaAtual(totalPaginasAudiosCurso)}
                                        disabled={paginaAtual === totalPaginasAudiosCurso}
                                    >
                                        <Icon icon="ep:d-arrow-right" />
                                    </button>
                                </li>
                            </ul>
                        </div>

                    </div>
                </div>
            </div>
            {showDeleteModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirmar Exclusão</h5>
                                <button type="button" className="btn-close" onClick={fecharModalExcluir} />
                            </div>
                            <div className="modal-body">
                                {carregandoImpacto ? (
                                    <div className="d-flex align-items-center justify-content-center gap-2 py-3">
                                        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                                        <span>Verificando vínculos do curso...</span>
                                    </div>
                                ) : impactoExclusao ? (
                                    <>
                                        <p className="mb-3">
                                            Curso: <strong>{impactoExclusao.cursoNome}</strong>
                                        </p>
                                        <div className="border rounded-3 p-3 mb-3 bg-light">
                                            <div><strong>{impactoExclusao.turmas}</strong> turma(s)</div>
                                            <div><strong>{impactoExclusao.matriculas}</strong> matrícula(s) ativa(s)</div>
                                            <div><strong>{impactoExclusao.audios}</strong> áudio(s)</div>
                                        </div>
                                        {impactoExclusao.podeExcluir ? (
                                            <div className="alert alert-danger mb-0">
                                                Ao confirmar, o curso e seus áudios serão excluídos definitivamente.
                                            </div>
                                        ) : (
                                            <div className="alert alert-warning mb-0">
                                                Este curso não pode ser excluído porque possui turmas ou matrículas vinculadas.
                                                Edite o curso existente em vez de criar outro.
                                            </div>
                                        )}
                                    </>
                                ) : null}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={fecharModalExcluir}>Cancelar</button>
                                <button
                                    className="btn btn-danger"
                                    onClick={deletarCurso}
                                    disabled={carregandoImpacto || !impactoExclusao?.podeExcluir || deletandoCurso}
                                >
                                    {deletandoCurso ? "Excluindo..." : "Excluir"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteAudioModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirmar Exclusão</h5>
                                <button type="button" className="btn-close" onClick={fecharModalExcluirAudio} />
                            </div>
                            <div className="modal-body">
                                Tem certeza que deseja excluir este áudio? Essa ação não poderá ser desfeita.
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={fecharModalExcluirAudio}>Cancelar</button>
                                <button className="btn btn-danger" onClick={deletarAudio}>Excluir</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer />
        </div>
        </>
    );
};

export default Audios;
